import "./login.component.scss";

import { DivWithSpinner, IntegratedDialogError } from "@tedivo/tedivo-ui";
import { IFields, TedivoForm, translateTedivoForm } from "@tedivo/tedivo-form";
import { IFormAndNode, createScreen } from "../../../helpers/createScreen";
import awsCognito, {
  IAwsAuthResponse,
} from "../../../../app/security/awsCognito";
import { getTranslation, i18nReactive } from "../../../../app/i18/i18tn";

import { I18nComponentRegisterer } from "@tedivo/tedivo-i18";
import ICustomError from "../../../types/ICustomError";
import { MultiStepLogin } from "./multiStepLogin";
import Services from "../../../../app/services";
import { getPreferencesValue } from "@tedivo/tedivo-preferences";
import globalStore from "../../../../app/stores/globalStore";
import goSquared from "../../../../app/tracking/goSquared";
import { passwordCompliesWithCriteria } from "@tedivo/tvd-api-models";
import { removeChildren } from "@tedivo/tedivo-dom-helpers";
import { routeFns } from "../../../../app/router/routes";
import router from "../../../../app/router";
import securityModule from "../../../../app/security/SecurityModule";
import { setAppTitle } from "../../../../app/setAppTitle";
import { showDetailedCriteriaCompliance } from "../../my-organization/change-password.component";
import topMessageElement from "../../../layout/top-tools/getTopMessageElement";
import { z } from "zod";

export class TVDAuthLoginComponent extends HTMLElement {
  public static observedAttributes = [];

  private i18nCR: I18nComponentRegisterer;
  private divWithSpinner: DivWithSpinner;
  private h1Node: HTMLHeadingElement;
  private didChangePassword = false;
  private prevLoginStatus: boolean | undefined = undefined;

  private currentFormAndNode?: { formId: string } & (
    | IFormAndNode<IAuthLoginFields>
    | IFormAndNode<IEmpty>
    | IFormAndNode<IAuthNewPassword>
    | IFormAndNode<IAuthVerifyMfa>
  );

  private onDisconnectedCalls: (() => void)[] = [];

  private dialogError: IntegratedDialogError = new IntegratedDialogError(
    this,
    false,
    getTranslation("general:common.close"),
  );

  /** Where to go after logging in, from the `returnUrl` querystring parameter */
  private returnUrl: string | undefined = undefined;

  constructor() {
    super();
    this.i18nCR = new I18nComponentRegisterer(i18nReactive);
    this.divWithSpinner = document.createElement("div-spinner-element");
    this.h1Node = document.createElement("h1");

    setAppTitle(getTranslation("auth:logIn"));
  }

  /** Replaces the UI */
  private setCurrentFormAndNode = (fan: typeof this.currentFormAndNode) => {
    this.currentFormAndNode = fan;

    if (fan?.formId === "login") {
      createLogoH1Replacement(this.h1Node);
      this.prepend(this.h1Node);
    }

    if (fan?.node) {
      this.divWithSpinner.setLoading(false);
      removeChildren(this.divWithSpinner);
      this.divWithSpinner.appendChild(fan.node);
    }
  };

  connectedCallback() {
    this.appendChild(this.divWithSpinner);
    this.showScreen();

    goSquared.trackPage("Log-In");

    securityModule.addEventListener("loggedIn", this.onShowScreen);
    securityModule.addEventListener("loggedOut", this.onShowScreen);
  }

  private onShowScreen = () => {
    this.showScreen(false);
  };

  /** Shows/Replaces the log-in screen based on securityModule.isLoggedIn, force = false */
  showScreen = (force = false) => {
    const { isLoggedIn } = securityModule;

    const queryString = router.getRouteQuerystring() as IViewStateLogin;
    this.returnUrl = parseReturnUrl(queryString.returnUrl);

    if (isLoggedIn) {
      // Session restored (e.g. page reloaded) or already logged in
      if (this.returnUrl) {
        this.navigateAfterLogin(this.returnUrl);
      } else {
        router.navigate(
          routeFns.myProfile(),
          globalStore.hasShownOrgSelector
            ? undefined
            : { showOrgSelector: true },
          true,
        );
      }
      return;
    }

    if (this.prevLoginStatus === isLoggedIn && !force) return;

    setAppTitle(getTranslation("auth:logIn"));
    goSquared.addEvent("Log-In - Show page");

    const state = router.currentState as IViewStateLogin;

    const multiStepLogin = new MultiStepLogin(
      this.i18nCR,
      state,
      queryString,
      this.onSubmitLoginForm,
    );

    const formAndNodeLogin = createLoginPart({
      multiStepLogin,
      i18nCR: this.i18nCR,
      initialCreateAccount: window.location.hash?.includes("newAccount"),
    });

    this.setCurrentFormAndNode({
      ...formAndNodeLogin,
      formId: "login",
    });

    this.prevLoginStatus = isLoggedIn;
  };

  /**
   * Navigates once logged in. Setting the MFA, having no enabled organizations or
   * having to pick an organization go to the profile first. Otherwise, to destinationUrl.
   */
  private navigateAfterLogin = (destinationUrl: string) => {
    const shouldShowMfa =
      securityModule.organizationMfaForAllUsers &&
      !securityModule.isMfaEnabled;

    if (shouldShowMfa) {
      router.navigate(routeFns.myProfile(true), undefined, true);
      return;
    }

    if (awsCognito.allEffectiveOrganizationsInfo.length === 0) {
      router.navigate(routeFns.myProfile(), undefined, true);
      return;
    }

    if (awsCognito.allOrganizationsInfo.length > 1) {
      const preferredOrgId = getPreferencesValue("preferredOrgId") as string;
      if (preferredOrgId) awsCognito.changeCurrentOrganization(preferredOrgId);

      if (!preferredOrgId || !awsCognito.currentOrganizationId) {
        router.navigate(routeFns.myProfile(), { showOrgSelector: true }, true);
        return;
      }
    }

    router.navigate(destinationUrl, undefined, true);
  };

  onSubmitLoginForm = async (values: IAuthLoginFields) => {
    this.divWithSpinner.setLoading(true);

    const initialAuthResp = await securityModule.authenticate(
      values.username,
      values.password,
    );

    try {
      goSquared.addEvent("Log-In - Submit credentials");
      await this.onSubmitAuthForm(initialAuthResp, values.username);
    } catch (e) {
      console.log(e);
      this.showError({
        errorCode: e as string,
        message: "",
        translationKey: "errors:loginAttemptIncorrect",
      });
    }

    this.divWithSpinner.setLoading(false);
  };

  onSubmitAuthForm = async (
    authResp: IAwsAuthResponse,
    username: string,
  ): Promise<IAwsAuthResponse["code"]> => {
    // console.log("authResp", authResp);

    switch (authResp.code) {
      case "ok":
        {
          if (this.didChangePassword) Services.accounts.notifyNewPassword();

          topMessageElement.planIsExpired = securityModule.planIsExpired;

          this.navigateAfterLogin(this.returnUrl || routeFns.myCloud());
        }
        break;

      case "failure":
        {
          if (authResp.error.name === "UserNotFoundException") {
            this.showError("auth:loggedInFailed");
            break;
          }

          const errorMessage = authResp.error.message || "";
          let translationKey = errorMessage;

          if (
            errorMessage === "ORG_DISABLED" ||
            errorMessage === "USER_DISABLED" ||
            errorMessage === "EXPIRED_SUBSCRIPTION"
          ) {
            translationKey = `enums:EnumEnabledStatus.${errorMessage}`;
          }

          this.showError({
            errorCode: "",
            message: errorMessage,
            translationKey,
          });
        }
        break;

      case "resetPassword":
        router.navigate(routeFns.forgotPassword());
        break;

      case "newPasswordRequired":
        this.setCurrentFormAndNode({
          ...createNewPasswordFields({
            h1Node: this.h1Node,
            i18nCR: this.i18nCR,
            onSubmitDataForm: async (newValues: IAuthNewPassword) => {
              this.divWithSpinner.setLoading(true);

              const nextAuthResp =
                await securityModule.setNewPasswordFromForceChange(
                  username,
                  newValues.password,
                  !!newValues.acceptsMarketing,
                );

              this.didChangePassword = true;

              await this.onSubmitAuthForm(nextAuthResp, username);
              this.divWithSpinner.setLoading(false);
            },
          }),
          formId: "newPassword",
        });
        break;

      case "totpRequired":
        this.setCurrentFormAndNode({
          ...createVerifyMfaFields(
            this.h1Node,
            async (newValues: IAuthVerifyMfa) => {
              this.divWithSpinner.setLoading(true);

              const nextAuthResp = await awsCognito.verifyTopTCode(
                String(newValues.code),
              );

              await this.onSubmitAuthForm(nextAuthResp, username);

              this.divWithSpinner.setLoading(false);
            },
            this.i18nCR,
          ),
          formId: "mfa",
        });
        break;
    }

    return authResp.code;
  };

  disconnectedCallback() {
    this.i18nCR.disconnect();

    this.onDisconnectedCalls.forEach((fn) => fn());

    securityModule.removeEventListener("loggedIn", this.onShowScreen);
    securityModule.removeEventListener("loggedOut", this.onShowScreen);
  }

  showError = (e: ICustomError) => {
    this.dialogError.show(
      getTranslation(`errors:errorHasOcurred`),
      getTranslation(typeof e === "string" ? e : e.translationKey),
    );

    // this.dialogError.onButtonClicked = () => {
    //   this.showScreen(true);
    // };

    console.error(e);
  };
}

customElements.define("tvd-auth-login-component", TVDAuthLoginComponent);

declare global {
  interface HTMLElementTagNameMap {
    "tvd-auth-login-component": TVDAuthLoginComponent;
  }
}

function createLoginPart({
  multiStepLogin,
  i18nCR,
  initialCreateAccount,
}: {
  multiStepLogin: MultiStepLogin;
  i18nCR: I18nComponentRegisterer;
  initialCreateAccount?: boolean;
}): IFormAndNode<IAuthLoginFields> {
  const { newAccountForm, node: newPartNode } = createNewHerePartNodes();

  const divCenteredInteractive = document.createElement("div");
  divCenteredInteractive.className = "login-screen-interactive";

  const divCentralFormsPart = document.createElement("div");
  divCentralFormsPart.className = "login-screen-split";

  divCenteredInteractive.appendChild(divCentralFormsPart);

  divCentralFormsPart.appendChild(multiStepLogin.holder);
  divCentralFormsPart.appendChild(createSlider());
  divCentralFormsPart.appendChild(newPartNode);

  type ISendToCreateAccount = { email: string };

  if (initialCreateAccount) {
    divCenteredInteractive.dataset.show = "newAccount";
  } else {
    multiStepLogin.focusOnEmail();
  }

  return {
    node: divCenteredInteractive,
  };

  function togglePart() {
    const currentPart = divCenteredInteractive.dataset.show || "";
    const isLogin = currentPart === "";

    if (isLogin) {
      divCenteredInteractive.dataset.show = "newAccount";
      newAccountForm?.focusOnFirstElement();
    } else {
      divCenteredInteractive.dataset.show = "";
      multiStepLogin.focusOnEmail();
    }
  }

  function createSlider(): HTMLElement {
    const newHerePart = document.createElement("div");
    newHerePart.className = "login-slider";

    const card1 = document.createElement("div");
    card1.className = "card-1";
    {
      const h2 = document.createElement("h2");
      i18nCR.addConsumer(h2, "general:welcomeComp.slider1.title", "innerHTML");

      const text = document.createElement("div");
      i18nCR.addConsumer(text, "general:welcomeComp.slider1.text", "innerHTML");

      const btn = document.createElement("sl-button");
      btn.className = "login-button-home";
      btn.variant = "primary";
      i18nCR.addConsumer(
        btn,
        "general:welcomeComp.slider1.button",
        "innerHTML",
      );

      btn.onclick = () => {
        togglePart();
      };

      newHerePart.appendChild(card1);
      card1.appendChild(h2);
      card1.appendChild(text);
      card1.appendChild(btn);
    }

    const card2 = document.createElement("div");
    card2.className = "card-2";
    {
      const h2 = document.createElement("h2");
      i18nCR.addConsumer(h2, "general:welcomeComp.slider2.title", "innerHTML");

      const text = document.createElement("div");
      i18nCR.addConsumer(text, "general:welcomeComp.slider2.text", "innerHTML");

      const btn = document.createElement("sl-button");
      btn.className = "login-button-home";
      btn.variant = "primary";
      i18nCR.addConsumer(
        btn,
        "general:welcomeComp.slider2.button",
        "innerHTML",
      );

      btn.onclick = () => {
        togglePart();
      };

      newHerePart.appendChild(card2);
      card2.appendChild(h2);
      card2.appendChild(text);
      card2.appendChild(btn);
    }

    return newHerePart;
  }

  function createNewHerePartNodes(): {
    newAccountForm: TedivoForm<ISendToCreateAccount>;
    node: HTMLElement;
  } {
    const newHerePart = document.createElement("div");
    newHerePart.className = "login-new-here-part ";

    const ossCard = document.createElement("div");
    ossCard.className = "centered-content";

    const h2 = document.createElement("h2");
    i18nCR.addConsumer(
      h2,
      "general:welcomeComp.createNewAccount.title",
      "innerHTML",
    );

    const newHere = document.createElement("div");
    newHere.className = "new-here-text";
    i18nCR.addConsumer(
      newHere,
      "general:welcomeComp.createNewAccount.text",
      "innerHTML",
    );

    const newAccountBtn = document.createElement("sl-button");
    newAccountBtn.variant = "primary";
    i18nCR.addConsumer(
      newAccountBtn,
      "general:welcomeComp.createNewAccount.button",
      "innerHTML",
    );

    const formValidator = z.object({
      email: z.string().email(),
    });

    const fields: IFields<ISendToCreateAccount> = [
      {
        type: "textBox",
        name: "email",
        label: "",
        placeholder: "general:common.workEmail",
      },
    ];

    const newAccountTedivoForm = new TedivoForm<ISendToCreateAccount>({
      fields: fields,
      onSubmit: onSubmitDataForm,
      formValidator: formValidator,
      submitButton: newAccountBtn,
    });

    newAccountBtn.addEventListener("click", () => {
      newAccountTedivoForm.doSubmitForm();
    });

    translateTedivoForm<ISendToCreateAccount>({
      tedivoForm: newAccountTedivoForm,
      i18nCR: i18nCR,
    });

    newHerePart.appendChild(ossCard);
    ossCard.appendChild(h2);
    ossCard.appendChild(newHere);
    ossCard.appendChild(newAccountTedivoForm.form);
    ossCard.appendChild(newAccountBtn);

    return { newAccountForm: newAccountTedivoForm, node: newHerePart };

    function onSubmitDataForm(vals: ISendToCreateAccount) {
      router.navigate(
        `${routeFns.newAccount()}?email=${vals.email}`,
        undefined,
        true,
      );
    }
  }
}

function createNewPasswordFields({
  h1Node,
  onSubmitDataForm,
  i18nCR,
}: {
  h1Node: HTMLHeadingElement;
  onSubmitDataForm: (values: IAuthNewPassword) => void;
  i18nCR: I18nComponentRegisterer;
}): IFormAndNode<IAuthNewPassword> {
  const AuthLoginFormValidator = z
    .object({
      password: z
        .string()
        .refine((pswd) => passwordCompliesWithCriteria(pswd).length === 0),
      retypePassword: z.string(),
      acceptsMarketing: z.boolean().optional(),
      acceptsConditions: z.boolean().refine((v) => !!v),
      acceptsPrivacyPolicy: z.boolean().refine((v) => !!v),
    })
    .refine((vals) => vals.password === vals.retypePassword, {
      path: ["retypePassword"],
    });

  const passReqs = document.createElement("div");
  passReqs.className = "oss-password-compliance-container";

  const passwordReqsTitle = document.createElement("strong");
  passwordReqsTitle.innerHTML = getTranslation("auth:passwordSchemaTitle");
  passReqs.appendChild(passwordReqsTitle);

  const ul = document.createElement("ul");
  ul.className = "oss-password-compliance";
  passReqs.appendChild(ul);

  const fields: IFields<IAuthNewPassword> = [
    [
      {
        type: "textBox",
        name: "password",
        autoCompleteAttribute: "new-password",
        label: "auth:password",
        isPassword: true,
        helpText: "auth:passwordSchema",
        inputListener: true,
      },
      {
        type: "textBox",
        name: "retypePassword",
        label: "auth:retypePassword",
        isPassword: true,
        helpText: "auth:passwordsMustMatch",
      },
    ],
    {
      name: "password",
      type: "node",
      node: passReqs,
    },
    {
      type: "checkbox",
      name: "acceptsConditions",
      label: "general:common.acceptsConditions",
      initialValue: false,
    },
    {
      type: "checkbox",
      name: "acceptsPrivacyPolicy",
      label: "general:common.acceptsPrivacyPolicy",
      initialValue: false,
    },
    {
      type: "checkbox",
      name: "acceptsMarketing",
      label: "general:common.acceptsMarketing",
      initialValue: false,
    },
  ];

  const screen = createScreen<IAuthNewPassword>({
    fields,
    onSubmitDataForm,
    formValidator: AuthLoginFormValidator,
    i18nCR,
    h1Text: "auth:setNewPassword",
    titleText: "auth:logIn",
    h1Node,
  });

  const tedivoForm = screen.form;
  if (tedivoForm) {
    showDetailedCriteriaCompliance(true, new Set(), ul);

    tedivoForm.onDataChange = (data, name) => {
      if (name === "password") {
        const criteriaNotMet = new Set(
          passwordCompliesWithCriteria(data.password),
        );
        showDetailedCriteriaCompliance(false, criteriaNotMet, ul);
      }
    };
  }

  return screen;
}

function createVerifyMfaFields(
  h1Node: HTMLHeadingElement,
  onSubmitDataForm: (values: IAuthVerifyMfa) => void,
  i18nCR: I18nComponentRegisterer,
): IFormAndNode<IAuthVerifyMfa> {
  const AuthMfaFormValidator = z
    .object({
      code: z.string(),
    })
    .refine((vals) => vals.code.length === 6);

  const fields: IFields<IAuthVerifyMfa> = [
    {
      type: "textBox",
      name: "code",
      label: "auth:mfa.mfaCode",
      autoCompleteAttribute: "one-time-code",
      isPassword: false,
    },
  ];

  const screen = createScreen<IAuthVerifyMfa>({
    fields,
    onSubmitDataForm,
    formValidator: AuthMfaFormValidator,
    i18nCR,
    h1Text: "auth:mfa.mfaCodePlaceholder",
    titleText: "auth:logIn",
    h1Node,
  });

  return screen;
}

function createLogoH1Replacement(h1Node: HTMLHeadingElement) {
  const icon1 = document.createElement("img");
  icon1.setAttribute("title", getTranslation("general:appName"));
  icon1.setAttribute("alt", getTranslation("general:appName"));
  icon1.setAttribute("width", "522");
  icon1.setAttribute("heigth", "136");
  icon1.setAttribute(
    "src",
    "/assets/images/tedivo-designer-horizontal-tagline-color.svg",
  );
  icon1.className = "tvd-h1-icon logo-light";

  const icon2 = document.createElement("img");
  icon2.setAttribute("title", getTranslation("general:appName"));
  icon2.setAttribute("alt", getTranslation("general:appName"));
  icon2.setAttribute("width", "522");
  icon2.setAttribute("heigth", "136");
  icon2.setAttribute(
    "src",
    "/assets/images/tedivo-designer-horizontal-tagline-color-knockout.svg",
  );
  icon2.className = "tvd-h1-icon logo-dark";

  removeChildren(h1Node);
  h1Node.appendChild(icon1);
  h1Node.appendChild(icon2);
}

/** Decodes the returnUrl. Only paths of this app are allowed, never the login page itself */
function parseReturnUrl(rawReturnUrl: string | undefined): string | undefined {
  if (!rawReturnUrl) return undefined;

  let returnUrl: string;
  try {
    returnUrl = decodeURIComponent(rawReturnUrl);
  } catch {
    return undefined;
  }

  if (!returnUrl.startsWith("/") || returnUrl.startsWith("//")) return undefined;

  const returnPath = returnUrl.split(/[?#]/)[0].replace(/\/+$/, "");
  if (returnPath === routeFns.login()) return undefined;

  return returnUrl;
}

interface IAuthLoginFields {
  username: string;
  password: string;
}

interface IAuthNewPassword {
  password: string;
  retypePassword: string;
  acceptsConditions: boolean;
  acceptsPrivacyPolicy: boolean;
  acceptsMarketing?: boolean;
}

interface IEmpty {
  id?: string;
}

interface IAuthVerifyMfa {
  code: string;
}

interface IViewStateLogin {
  email?: string;
  password?: string;
  fromVerification?: boolean;
  returnUrl?: string;
  hint?: string;
}
